/*
  LIOR "Worn together" cart drawer upsell (markup: snippets/lior-cart-upsell.liquid).
  Event delegation on document, so it keeps working after the theme re-renders the drawer.
  Add flow mirrors the theme's own product-form (assets main JS):
    POST routes.cart_add_url (FormData: id, quantity, sections, sections_url)
    -> publish(PUB_SUB_EVENTS.cartUpdate, ...) -> cartDrawer.renderContents(response, true)
  Swipe progress bar (2026-10-01): the thin bar under the row ([data-lior-upsell-progress]) shows
  the visible share of the row (thumb width = visible / total) and moves with the scroll, like the
  product-page carousel (assets/lior-carousel.js). Updated rAF-throttled on scroll (captured on
  document, so re-rendered tracks need no re-binding), on resize, via a ResizeObserver per track,
  and after every drawer re-render (MutationObserver on <cart-drawer> + cart-update pub/sub).
  When nothing overflows, .lior-upsell--static hides the bar.
  Photo fit (2026-10-02, owner: "the images on the upsells are cropped ... look how much space there is"): the
  add-on photos are never cropped (object-fit: contain, assets/lior-cart-clean.css) and their box takes the drawer's
  spare height first, up to 1.35x the card width (1.7x since 2026-10-15); any height left after that stayed blank
  under the footer until 2026-10-15 and now sits above the totals (fixed
  section gaps, no stretchy gap: owner 2026-10-02 "Same spacing everywhere" + "now there is space below the cart
  item"). See fitPhotos() below.
  Line-item photo (2026-10-10, owner: "too much white space below with only one item in cart ... just fill the space
  with the item in cart, and when they add a 2nd and 3rd the cart items become smaller"): once the add-on photos are
  a full square, the spare height goes into the photo of the cart item(s), shared equally, before the add-on boxes
  grow any further. More items leave less spare, so the photos shrink back to their normal size by themselves.
  2026-10-15 (owner: "what is this white space", after the ADORN banner and the code field left the cart): the add-on
  boxes may grow up to 1.7x the card width (was 1.35x), and the footer is pinned to the bottom of the screen again
  (assets/lior-cart-clean.css, body flex 1 1 auto), so any height still left sits above the totals, never under the
  trust strip.
*/
(function () {
  'use strict';

  if (window.liorCartUpsellLoaded) return;
  window.liorCartUpsellLoaded = true;

  var ADD_SELECTOR = '[data-lior-upsell-add]';
  var CARD_SELECTOR = '.lior-upsell__card';
  var ROOT_SELECTOR = '.lior-upsell';
  var TRACK_SELECTOR = '.lior-upsell__track';
  var PROGRESS_SELECTOR = '[data-lior-upsell-progress]';
  var THUMB_SELECTOR = '[data-lior-upsell-progress-thumb]';

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function getCart() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getSectionIds(cart) {
    try {
      if (cart && typeof cart.getSectionsToRender === 'function') {
        return cart.getSectionsToRender().map(function (section) {
          return section.id;
        });
      }
    } catch (err) {
      /* fall through to the theme defaults */
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function genericError() {
    return (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
  }

  function setError(card, message) {
    if (!card) return;
    var el = card.querySelector('.lior-upsell__error');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function setLoading(button, loading) {
    if (!button) return;
    button.disabled = !!loading;
    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    } else {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }

  function buildRequest(cart, variantId) {
    var config = null;
    try {
      config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : null;
    } catch (err) {
      config = null;
    }
    if (!config) {
      config = { method: 'POST', headers: { Accept: 'application/javascript' } };
    }
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');
    if (cart) {
      body.append('sections', getSectionIds(cart).join(','));
      body.append('sections_url', window.location.pathname);
      try {
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      } catch (err) {
        /* non-critical */
      }
    }
    config.body = body;
    return config;
  }

  function ensureSections(cart, data) {
    if (!cart || data.sections) return Promise.resolve(data);
    return fetch(cartUrl() + '?sections=' + getSectionIds(cart).join(','))
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        data.sections = sections;
        return data;
      });
  }

  function publishCartUpdate(variantId, data) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: String(variantId),
          cartData: data
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  function handleAdd(button) {
    var card = button.closest(CARD_SELECTOR);
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var cart = getCart();
    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';

    setError(card, '');
    setLoading(button, true);

    fetch(url, buildRequest(cart, variantId))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data || data.status) {
          setError(card, (data && (data.description || data.message)) || genericError());
          return;
        }
        if (!cart) {
          window.location = cartUrl();
          return;
        }
        return ensureSections(cart, data).then(function (fullData) {
          publishCartUpdate(variantId, fullData);
          if (fullData.sections && typeof cart.renderContents === 'function') {
            cart.renderContents(fullData, true);
          } else {
            window.location = cartUrl();
          }
        });
      })
      .catch(function (err) {
        console.error(err);
        setError(card, genericError());
      })
      .finally(function () {
        if (button.isConnected) setLoading(button, false);
      });
  }

  /* ---------- swipe progress bar ---------- */

  var resizeObserver = null;
  try {
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(function () {
        scheduleProgress();
      });
    }
  } catch (err) {
    resizeObserver = null;
  }

  function updateProgress(root) {
    var track = root.querySelector(TRACK_SELECTOR);
    if (!track) return;
    if (resizeObserver && !track.__liorUpObserved) {
      track.__liorUpObserved = true;
      resizeObserver.observe(track);
    }
    var scrollWidth = track.scrollWidth;
    var clientWidth = track.clientWidth;
    if (!clientWidth) return; /* not laid out yet; the ResizeObserver calls back once it is */
    var max = scrollWidth - clientWidth;
    var overflowing = max > 2;
    root.classList.toggle('lior-upsell--static', !overflowing);
    var bar = root.querySelector(PROGRESS_SELECTOR);
    var thumb = bar ? bar.querySelector(THUMB_SELECTOR) : null;
    if (!thumb) return;
    var visible = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
    var position = overflowing ? Math.min(1, Math.max(0, Math.abs(track.scrollLeft) / max)) : 0;
    var widthPct = visible * 100;
    thumb.style.width = widthPct + '%';
    thumb.style.left = position * (100 - widthPct) + '%';
  }

  function updateAllProgress() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      try {
        updateProgress(roots[i]);
      } catch (err) {
        /* never break the drawer over the progress bar */
      }
    }
  }

  var progressQueued = false;
  function scheduleProgress() {
    if (progressQueued) return;
    progressQueued = true;
    var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    raf(function () {
      progressQueued = false;
      updateAllProgress();
    });
  }

  /* scroll does not bubble, so listen in the capture phase: works for tracks inserted later */
  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (target && target.nodeType === 1 && typeof target.matches === 'function' && target.matches(TRACK_SELECTOR)) {
        scheduleProgress();
      }
    },
    { capture: true, passive: true }
  );
  window.addEventListener('resize', scheduleProgress);
  window.addEventListener('load', scheduleProgress);
  document.addEventListener('shopify:section:load', scheduleProgress);

  function watchDrawer() {
    if (!('MutationObserver' in window)) return;
    var host = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (!host) return;
    new MutationObserver(scheduleProgress).observe(host, { childList: true, subtree: true });
  }

  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(scheduleProgress, 0);
      });
    }
  } catch (err) {
    /* optional */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      watchDrawer();
      scheduleProgress();
    });
  } else {
    watchDrawer();
    scheduleProgress();
  }

  /* ---------- add-on photo fit ----------
     The room for the drawer body is the panel (.drawer__inner, the full-height column) minus the header and the
     footer (bodyRoom). Since 2026-10-02 night (owner: "now there is space below the cart item") the body was only as
     tall as its content (assets/lior-cart-clean.css: flex 0 1 auto), so every gap between the sections is fixed and
     the footer followed the add-ons; since 2026-10-15 the body fills the room (flex 1 1 auto) and the footer sits at
     the bottom of the panel, the gaps between the sections stay fixed. Every spare pixel of that room goes into the add-on photo boxes: from FIT_MIN up
     to a full square (box height = card width) and then on up to FIT_MAX x the card width (the photo stays whole and
     centred, object-fit: contain; all cards share the one height). What is left after that sits under the add-ons,
     above the totals (blank under the footer until 2026-10-15). When the content does not fit even at FIT_MIN (e.g. two items on a short
     phone) the boxes stay at FIT_MIN, the body shrinks and scrolls and the footer stays pinned, as before.
     Order since 2026-10-10: (1) add-on boxes up to a full square, (2) the line-item photos take the spare (equal
     share each, up to ITEM_MAX and never so wide that the text column gets narrower than ITEM_TEXT_MIN; written as
     --lior-item-photo on <cart-drawer>, switched on by data-lior-item-fit), (3) the add-on boxes take what is still
     left, up to FIT_MAX. A row only gets taller once its photo is taller than the text beside it, so step 2 sets a
     size, measures and corrects, and never leaves the content taller than the room.
     The height is written as --lior-up-photo on <cart-drawer> and switched on by its data-lior-up-fit attribute.
     <cart-drawer> survives the theme's re-renders (they replace #CartDrawer's contents only), so a re-rendered
     drawer starts at the last size and is corrected in the same frame. Runs synchronously from MutationObserver /
     ResizeObserver callbacks (both before paint; the ResizeObserver watches the panel, header and footer, whose
     sizes never depend on the photo size, so a fit never re-triggers itself), on resize / orientation /
     visualViewport resize, font loads and page load. The closed drawer is laid out (visibility: hidden), so the size
     is already right when it opens. While lior-drawer-keyboard.js holds the panel above the keyboard (inline height
     on .drawer__inner) the size is left as it is. Any error leaves the CSS default (whole photo, fixed box). */

  var FIT_MIN = 88;
  var FIT_MAX = 2.4; /* x the card width (1.35 until 2026-10-15, 1.7 until 2026-10-17); narrow phones (360px) need ~2.4 */
  var SHORT_MAX = 820.5; /* px panel height: the short-cart layout (assets/lior-cart-clean.css, data-lior-short) */
  var MID_MIN = 761; /* px: from here up to SHORT_MAX also data-lior-mid (was min-height: 761px) */
  var ITEM_MAX = 180; /* px: the largest line-item photo */
  var ITEM_TEXT_MIN = 212; /* px the text column keeps: 14 padding + 120 quantity box + 8 + the "Remove" link (69) */
  var fitRunning = false;
  var fitObserved = [];
  var fitDrawer = null;
  var fitResizeObserver = null;
  var fitMutationObserver = null;

  function bodyContentHeight(body) {
    var cs = window.getComputedStyle(body);
    var h = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    for (var el = body.firstElementChild; el; el = el.nextElementSibling) {
      var s = window.getComputedStyle(el);
      if (s.display === 'none' || s.position === 'absolute' || s.position === 'fixed') continue;
      h += el.getBoundingClientRect().height + (parseFloat(s.marginTop) || 0) + (parseFloat(s.marginBottom) || 0);
    }
    return h;
  }

  function px(v) {
    return parseFloat(v) || 0;
  }

  /* the height the body may take: the panel's inner height minus everything else in the panel (header, footer) and
     the body's own margins / borders. Falls back to the body's own height if the markup is not the expected one. */
  function bodyRoom(panel, body) {
    if (!panel || body.parentElement !== panel) return body.clientHeight;
    var pcs = window.getComputedStyle(panel);
    var h = panel.getBoundingClientRect().height - px(pcs.borderTopWidth) - px(pcs.borderBottomWidth) - px(pcs.paddingTop) - px(pcs.paddingBottom);
    for (var el = panel.firstElementChild; el; el = el.nextElementSibling) {
      var s = window.getComputedStyle(el);
      if (s.display === 'none' || s.position === 'absolute' || s.position === 'fixed') continue;
      h -= px(s.marginTop) + px(s.marginBottom);
      if (el === body) h -= px(s.borderTopWidth) + px(s.borderBottomWidth);
      else h -= el.getBoundingClientRect().height;
    }
    return h;
  }

  function directChild(parent, cls) {
    for (var el = parent.firstElementChild; el; el = el.nextElementSibling) {
      if (el.classList && el.classList.contains(cls)) return el;
    }
    return null;
  }

  function clearItemFit(drawer) {
    if (drawer.hasAttribute('data-lior-item-fit')) drawer.removeAttribute('data-lior-item-fit');
    if (drawer.style.getPropertyValue('--lior-item-photo')) drawer.style.removeProperty('--lior-item-photo');
  }

  function setItemFit(drawer, px) {
    var v = px + 'px';
    if (drawer.style.getPropertyValue('--lior-item-photo') !== v) drawer.style.setProperty('--lior-item-photo', v);
    if (!drawer.hasAttribute('data-lior-item-fit')) drawer.setAttribute('data-lior-item-fit', '');
  }

  function clearFit(drawer) {
    clearItemFit(drawer);
    if (drawer.hasAttribute('data-lior-up-fit')) drawer.removeAttribute('data-lior-up-fit');
    if (drawer.style.getPropertyValue('--lior-up-photo')) drawer.style.removeProperty('--lior-up-photo');
  }

  /* step 2 of the fit: the spare height left with square add-on photos goes into the line-item photos */
  function fitItems(drawer, body, avail) {
    var items = body.querySelectorAll('.cart-drawer-item');
    var n = items.length;
    if (!n) return;
    var itemMedia = items[0].querySelector('.cart-item__media');
    if (!itemMedia) return;
    var base = Math.round(itemMedia.getBoundingClientRect().width); /* the CSS size (item fit is off right now) */
    var cap = Math.min(ITEM_MAX, Math.floor(items[0].getBoundingClientRect().width - ITEM_TEXT_MIN));
    var spare = Math.floor(avail - bodyContentHeight(body));
    if (!(base > 0) || cap <= base || spare <= 0) return;
    /* a row grows only once its photo is taller than the text beside it: start from the shortest row's height */
    var rowH = Infinity;
    for (var i = 0; i < n; i++) rowH = Math.min(rowH, items[i].getBoundingClientRect().height);
    var size = Math.min(cap, Math.floor(Math.max(base, rowH) + spare / n));
    var best = 0; /* the largest size that measured as fitting */
    for (var k = 0; k < 4 && size > base; k++) {
      setItemFit(drawer, size);
      var rest = Math.floor(avail - bodyContentHeight(body));
      if (rest >= 0 && size > best) best = size;
      if (rest >= 0 && (size >= cap || rest < n)) break;
      var next = Math.min(cap, size + (rest >= 0 ? Math.floor(rest / n) : -Math.ceil(-rest / n)));
      if (next === size) break;
      size = next;
    }
    if (size <= base) { clearItemFit(drawer); return; }
    setItemFit(drawer, size);
    if (avail - bodyContentHeight(body) < 0) { /* never taller than the room: step back until it fits */
      for (var g = 0; g < 6 && size > base && avail - bodyContentHeight(body) < 0; g++) {
        size = Math.max(base, size - Math.ceil((bodyContentHeight(body) - avail) / n));
        if (size <= base) clearItemFit(drawer); else setItemFit(drawer, size);
      }
    }
    /* a wider photo narrows the text beside it and can wrap the name: then a smaller photo does not give the height
       back. Go back to the largest size that did fit, or to the normal size. */
    if (drawer.hasAttribute('data-lior-item-fit') && avail - bodyContentHeight(body) < 0) {
      if (best > base) setItemFit(drawer, best);
      else clearItemFit(drawer);
    }
  }

  /* the browser picks the photo file from the img "sizes" value. Liquid prints the normal size (88px); a grown photo
     gets its real size here, upward only, so a larger file that is already loaded is never swapped back. */
  function syncItemSizes(drawer, body) {
    if (!drawer.hasAttribute('data-lior-item-fit')) return;
    var want = parseInt(drawer.style.getPropertyValue('--lior-item-photo'), 10);
    if (!(want > 0)) return;
    var imgs = body.querySelectorAll('.cart-item__image[srcset]');
    for (var i = 0; i < imgs.length; i++) {
      var cur = parseInt(imgs[i].getAttribute('sizes'), 10) || 0;
      if (want > cur) imgs[i].setAttribute('sizes', want + 'px');
    }
  }

  function setFit(drawer, h) {
    var v = h + 'px';
    if (drawer.style.getPropertyValue('--lior-up-photo') !== v) drawer.style.setProperty('--lior-up-photo', v);
    if (!drawer.hasAttribute('data-lior-up-fit')) drawer.setAttribute('data-lior-up-fit', '');
  }

  function observeParts(list) {
    /* re-attached when a re-render replaced the header / footer (or the panel) */
    if (!fitResizeObserver) return;
    var same = list.length === fitObserved.length;
    for (var i = 0; same && i < list.length; i++) same = list[i] === fitObserved[i];
    if (same) return;
    fitResizeObserver.disconnect();
    fitObserved = list;
    for (var j = 0; j < list.length; j++) if (list[j]) fitResizeObserver.observe(list[j]);
  }

  function observeDrawer(drawer) {
    /* re-renders (childList) and open / close / is-empty (class); re-attached if the drawer element is replaced
       (theme editor section reload) */
    if (!fitMutationObserver || drawer === fitDrawer) return;
    fitMutationObserver.disconnect();
    fitDrawer = drawer;
    fitMutationObserver.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function setFlag(el, name, on) {
    if (on) { if (!el.hasAttribute(name)) el.setAttribute(name, ''); }
    else if (el.hasAttribute(name)) el.removeAttribute(name);
  }

  /* 2026-10-17 (owner: "White space in cart not fixed", iPhone Chrome): the short-cart layout used to be a
     max-height: 820px media query. On iPhone the query sees the short height with the toolbars shown while the drawer
     is taller once they hide, so the compact header / footer / photos were used in a tall drawer and ~75px stayed
     blank above the totals. The layout now follows the panel's own height (it is the full-height column, its size
     never depends on these flags, so this cannot loop). Left alone while the keyboard fit holds the panel. */
  function setShortFlags(drawer, panel) {
    if (!panel || panel.style.getPropertyValue('height')) return;
    var h = panel.getBoundingClientRect().height;
    if (!(h > 0)) return;
    setFlag(drawer, 'data-lior-short', h <= SHORT_MAX);
    setFlag(drawer, 'data-lior-mid', h >= MID_MIN && h <= SHORT_MAX);
  }

  function fitPhotosNow() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    observeDrawer(drawer);
    var body = drawer.querySelector('.cart-drawer__body');
    var panel = drawer.querySelector('.drawer__inner');
    setShortFlags(drawer, panel);
    observeParts(panel ? [panel, directChild(panel, 'drawer__header'), directChild(panel, 'drawer__footer')] : []);
    var upsell = body ? directChild(body, 'lior-upsell') : null;
    var media = upsell ? upsell.querySelector('.lior-upsell__media') : null;
    if (drawer.classList.contains('is-empty') || !media) {
      clearFit(drawer);
      return;
    }
    if (panel && panel.style.getPropertyValue('height')) return; /* keyboard fit active: keep the size */
    var avail = bodyRoom(panel, body);
    var full = Math.floor(media.getBoundingClientRect().width);
    if (!(avail > 0) || !full) return; /* not laid out */
    var min = Math.min(FIT_MIN, full);
    var max = Math.max(min, Math.floor(full * FIT_MAX));
    /* (1) line items at their normal size, add-on boxes up to a full square */
    clearItemFit(drawer);
    fitBox(drawer, body, media, avail, min, Math.max(min, full));
    /* (2) spare left with square add-on photos goes into the line-item photos */
    if (media.getBoundingClientRect().height >= full - 0.5) fitItems(drawer, body, avail);
    /* (3) what is still left goes into the add-on boxes, as before */
    fitBox(drawer, body, media, avail, min, max);
    syncItemSizes(drawer, body);
  }

  function fitBox(drawer, body, media, avail, min, max) {
    for (var i = 0; i < 3; i++) {
      var cur = media.getBoundingClientRect().height;
      var want = Math.max(min, Math.min(max, Math.floor(cur + avail - bodyContentHeight(body))));
      if (drawer.hasAttribute('data-lior-up-fit') && Math.abs(want - cur) < 0.5) break;
      setFit(drawer, want);
    }
  }

  function fitPhotos() {
    if (fitRunning) return;
    fitRunning = true;
    try {
      fitPhotosNow();
    } catch (err) {
      /* never break the drawer over the photo size */
    }
    fitRunning = false;
  }

  try {
    if ('ResizeObserver' in window) fitResizeObserver = new ResizeObserver(fitPhotos);
  } catch (err) {
    fitResizeObserver = null;
  }
  try {
    if ('MutationObserver' in window) fitMutationObserver = new MutationObserver(fitPhotos);
  } catch (err) {
    fitMutationObserver = null;
  }

  /* everything here is optional: an error must never stop the Add to cart handler below from being registered */
  try {
    window.addEventListener('resize', fitPhotos);
    window.addEventListener('orientationchange', fitPhotos);
    window.addEventListener('load', fitPhotos);
    document.addEventListener('shopify:section:load', fitPhotos);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', fitPhotos);
    if (document.fonts) {
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', fitPhotos);
      if (document.fonts.ready) document.fonts.ready.then(fitPhotos);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fitPhotos);
    else fitPhotos();
  } catch (err) {
    /* optional */
  }

  /* ---------- add to cart ---------- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var button = target.closest(ADD_SELECTOR);
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    try {
      handleAdd(button);
    } catch (err) {
      console.error(err);
      setLoading(button, false);
      setError(button.closest(CARD_SELECTOR), genericError());
    }
  });
})();
